import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import communityApi from '../api';

const CommunityAuthContext = createContext(null);
const USER_STORAGE_KEY = 'community_user';
const AUTH_EXPIRED_EVENT = 'community:auth-expired';

// Use sessionStorage so the cached user is cleared when the browser/tab is closed.
// The HttpOnly refresh cookie (a session cookie with no max_age) is also cleared on
// browser close, so both sides stay in sync.
const userStorage = typeof window !== 'undefined' ? window.sessionStorage : null;

const resolveIsAdmin = (userData) =>
  Boolean(
    userData?.role === 'admin' ||
    userData?.is_staff ||
    userData?.is_superuser
  );

export const useCommunityAuth = () => useContext(CommunityAuthContext);

export const CommunityAuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() => {
    if (!userStorage) {
      return null;
    }
    const storedUser = userStorage.getItem(USER_STORAGE_KEY);
    if (!storedUser) {
      return null;
    }
    try {
      return JSON.parse(storedUser);
    } catch (err) {
      userStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persistUser = useCallback((nextUser) => {
    setUser(nextUser || null);
    if (!userStorage) {
      return;
    }
    if (nextUser) {
      userStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      userStorage.removeItem(USER_STORAGE_KEY);
    }
  }, []);

  const hydrateSession = useCallback(async () => {
    try {
      const response = await communityApi.auth.session();
      if (response.data?.user) {
        persistUser(response.data.user);
      } else {
        // Server explicitly confirmed there is no valid session.
        persistUser(null);
      }
    } catch (err) {
      // A network error or unexpected failure — do NOT clear the user here.
      // The existing localStorage state is kept so the UI doesn't flash logged-out
      // on a transient connectivity issue. The session will be re-validated on the
      // next successful request via the axios 401 interceptor.
      throw err;
    }
  }, [persistUser]);

  useEffect(() => {
    let active = true;

    hydrateSession()
      .catch(() => null)
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [hydrateSession]);

  const login = useCallback((userData) => {
    persistUser(userData);
    queryClient.invalidateQueries({ queryKey: ['community'] });
  }, [persistUser, queryClient]);

  const logout = useCallback(async () => {
    try {
      await communityApi.auth.logout();
    } catch (err) {
      // Ignore logout errors; cookies may already be cleared.
    }
    persistUser(null);
    queryClient.removeQueries({ queryKey: ['community'] });
  }, [persistUser, queryClient]);

  const refreshSession = useCallback(async () => {
    try {
      await hydrateSession();
    } catch (err) {
      // Swallow refresh errors; caller can inspect current user state.
    }
  }, [hydrateSession]);

  const handleAuthExpired = useCallback(() => {
    persistUser(null);
    queryClient.removeQueries({ queryKey: ['community'] });
  }, [persistUser, queryClient]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, [handleAuthExpired]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    refreshSession,
    isAdmin: resolveIsAdmin(user),
    isAuthenticated: Boolean(user),
  }), [user, loading, login, logout, refreshSession]);

  return (
    <CommunityAuthContext.Provider value={contextValue}>
      {children}
    </CommunityAuthContext.Provider>
  );
};

export default CommunityAuthContext;
